import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { listTestCaseVersions } from './versionHistoryApi';
import type { TestCaseVersion } from './types';

interface TestCaseVersionHistoryPanelProps {
  open: boolean;
  testCaseId: string | null;
  testCaseThreadId: string | null;
  refreshKey?: number;
  showFeedbackAction?: boolean;
  onFeedback?: () => void;
  onClose: () => void;
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: 'min(1400px, 96vw)',
    maxWidth: 1400,
  },
  versionItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  badges: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  field: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
  },
  fieldLabel: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  feedbackText: {
    whiteSpace: 'pre-wrap',
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
  },
  pairedTableContainer: {
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  pairedTable: {
    width: '100%',
    tableLayout: 'fixed',
  },
  pairedHeaderCell: {
    width: '50%',
    fontWeight: 700,
    backgroundColor: theme.palette.background.default,
    borderRight: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderRight: 0,
    },
  },
  pairedValueCell: {
    width: '50%',
    verticalAlign: 'top',
    whiteSpace: 'normal',
    borderRight: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderRight: 0,
    },
  },
  dialogTitle: {
    paddingRight: theme.spacing(1),
  },
  dialogTitleContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
  },
  content: {
    maxHeight: '75vh',
    overflowY: 'auto',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    alignItems: 'start',
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  versionList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  preview: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  dialogActions: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  closeButton: {
    minWidth: 96,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[700],

    '&:hover': {
      backgroundColor: theme.palette.grey[800],
    },

    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
}));

function formatDate(value: string): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function renderValue(value: string | string[]) {
  if (Array.isArray(value)) {
    return (
      <>
        {value.map((item, index) => (
          <Typography key={`${item}-${index}`} variant="body2">
            • {item}
          </Typography>
        ))}
      </>
    );
  }

  return <Typography variant="body2">{value || '—'}</Typography>;
}

function normalizeDisplayItems(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value) {
    return [];
  }

  return [value];
}

function normalizeFieldKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

export const TestCaseVersionHistoryPanel = ({
  open,
  testCaseId,
  testCaseThreadId,
  onClose,
  showFeedbackAction = false,
  onFeedback,
  refreshKey = 0,
}: TestCaseVersionHistoryPanelProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  const [versions, setVersions] = useState<TestCaseVersion[]>([]);

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null,
  );

  const [loading, setLoading] = useState(false);

  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open || !testCaseId || !testCaseThreadId) {
      setVersions([]);
      setSelectedVersionId(null);
      setLoadError(false);
      setLoading(false);

      return undefined;
    }

    let cancelled = false;

    const loadHistory = async () => {
      setLoading(true);
      setLoadError(false);

      try {
        const response = await listTestCaseVersions(
          testCaseThreadId,
          testCaseId,
        );

        if (cancelled) {
          return;
        }

        setVersions(response.versions);

        const currentVersion =
          response.versions.find(version => version.isCurrent) ??
          response.versions[0];

        setSelectedVersionId(currentVersion?.versionId ?? null);
      } catch {
        if (!cancelled) {
          setVersions([]);
          setSelectedVersionId(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [open, refreshKey, testCaseId, testCaseThreadId]);

  const selectedVersion = useMemo(
    () =>
      versions.find(version => version.versionId === selectedVersionId) ?? null,
    [versions, selectedVersionId],
  );

  const stepsField = useMemo(
    () =>
      selectedVersion?.fields.find(field => {
        const key = normalizeFieldKey(field.key);

        return key === 'steps' || key === 'teststeps';
      }),
    [selectedVersion],
  );

  const expectedResultsField = useMemo(
    () =>
      selectedVersion?.fields.find(field => {
        const key = normalizeFieldKey(field.key);

        return key === 'expectedresult' || key === 'expectedresults';
      }),
    [selectedVersion],
  );

  const orderedRemainingFields = useMemo(() => {
    const fields =
      selectedVersion?.fields.filter(field => {
        const key = normalizeFieldKey(field.key);

        return (
          key !== 'steps' &&
          key !== 'teststeps' &&
          key !== 'expectedresult' &&
          key !== 'expectedresults'
        );
      }) ?? [];

    const idFields = fields.filter(field => {
      const key = normalizeFieldKey(field.key);

      return key === 'testcaseid' || key === 'id';
    });

    const otherFields = fields.filter(field => {
      const key = normalizeFieldKey(field.key);

      return key !== 'testcaseid' && key !== 'id';
    });

    return [...idFields, ...otherFields];
  }, [selectedVersion]);

  const preconditionsIndex = orderedRemainingFields.findIndex(
    field => normalizeFieldKey(field.key) === 'preconditions',
  );

  const fieldsBeforeSteps = useMemo(
    () =>
      preconditionsIndex >= 0
        ? orderedRemainingFields.slice(0, preconditionsIndex + 1)
        : orderedRemainingFields,
    [orderedRemainingFields, preconditionsIndex],
  );

  const fieldsAfterSteps = useMemo(
    () =>
      preconditionsIndex >= 0
        ? orderedRemainingFields.slice(preconditionsIndex + 1)
        : [],
    [orderedRemainingFields, preconditionsIndex],
  );

  let dialogContent;

  if (loading) {
    dialogContent = (
      <Box className={classes.centered}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (loadError) {
    dialogContent = (
      <Alert severity="error">
        {t('testCaseGeneration.history.loadFailed')}
      </Alert>
    );
  } else if (versions.length === 0) {
    dialogContent = (
      <Alert severity="info">{t('testCaseGeneration.history.empty')}</Alert>
    );
  } else {
    dialogContent = (
      <Box className={classes.layout}>
        <Box className={classes.versionList}>
          <List disablePadding>
            {versions.map(version => (
              <ListItem
                key={version.versionId}
                button
                selected={version.versionId === selectedVersionId}
                onClick={() => setSelectedVersionId(version.versionId)}
                className={classes.versionItem}
              >
                <ListItemText
                  primary={t('testCaseGeneration.history.version', {
                    number: version.versionNumber,
                  })}
                  secondary={
                    <>
                      <Typography variant="caption" display="block">
                        {formatDate(version.createdAt)}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        className={classes.feedbackText}
                      >
                        {version.feedbackInstruction ||
                          t('testCaseGeneration.history.initialVersion')}
                      </Typography>
                    </>
                  }
                  secondaryTypographyProps={{
                    style: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box className={classes.preview}>
          {selectedVersion && (
            <>
              <Box className={classes.previewHeader}>
                <Box>
                  <Typography variant="h6">
                    {t('testCaseGeneration.history.version', {
                      number: selectedVersion.versionNumber,
                    })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(selectedVersion.createdAt)}
                  </Typography>
                </Box>
                <Box className={classes.badges}>
                  <Chip
                    size="small"
                    color={
                      selectedVersion.status === 'APPROVED'
                        ? 'primary'
                        : 'default'
                    }
                    label={
                      selectedVersion.status === 'APPROVED'
                        ? t('testCaseGeneration.generated.statusApproved')
                        : t('testCaseGeneration.generated.statusDraft')
                    }
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(
                      `testCaseGeneration.history.lifecycleStatus.${selectedVersion.lifecycleStatus}`,
                    )}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    label={t(
                      `testCaseGeneration.history.source.${selectedVersion.source}`,
                    )}
                  />

                  {selectedVersion.isCurrent && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={t('testCaseGeneration.history.current')}
                    />
                  )}
                </Box>
              </Box>
              <Paper variant="outlined" className={classes.field}>
                <Typography variant="body2" className={classes.fieldLabel}>
                  {t('testCaseGeneration.history.feedbackInstruction')}
                </Typography>
                <Typography variant="body2" className={classes.feedbackText}>
                  {selectedVersion.feedbackInstruction ||
                    t('testCaseGeneration.history.initialVersion')}
                </Typography>
              </Paper>
              <Divider />
              <Box mt={2}>
                {fieldsBeforeSteps.map(field => (
                  <Paper
                    key={field.key}
                    variant="outlined"
                    className={classes.field}
                  >
                    <Typography variant="body2" className={classes.fieldLabel}>
                      {field.label}
                    </Typography>

                    {renderValue(field.value)}
                  </Paper>
                ))}

                {(stepsField || expectedResultsField) &&
                  (() => {
                    const steps = normalizeDisplayItems(stepsField?.value);

                    const expectedResults = normalizeDisplayItems(
                      expectedResultsField?.value,
                    );

                    const rowCount = Math.max(
                      steps.length,
                      expectedResults.length,
                      1,
                    );

                    return (
                      <Box className={classes.pairedTableContainer}>
                        <Table size="small" className={classes.pairedTable}>
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.pairedHeaderCell}>
                                {stepsField?.label ?? 'Steps'}
                              </TableCell>

                              <TableCell className={classes.pairedHeaderCell}>
                                {expectedResultsField?.label ??
                                  'Expected Results'}
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {Array.from({ length: rowCount }, (_, index) => (
                              <TableRow key={index}>
                                <TableCell className={classes.pairedValueCell}>
                                  <Typography variant="body2">
                                    {steps[index] || '—'}
                                  </Typography>
                                </TableCell>

                                <TableCell className={classes.pairedValueCell}>
                                  <Typography variant="body2">
                                    {expectedResults[index] || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })()}

                {fieldsAfterSteps.map(field => (
                  <Paper
                    key={field.key}
                    variant="outlined"
                    className={classes.field}
                  >
                    <Typography variant="body2" className={classes.fieldLabel}>
                      {field.label}
                    </Typography>

                    {renderValue(field.value)}
                  </Paper>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogTitleContent}>
          <Typography variant="h5" className={classes.title}>
            {t('testCaseGeneration.history.title', {
              id: testCaseId ?? '',
            })}
          </Typography>

          <IconButton
            size="small"
            aria-label={t('testCaseGeneration.history.close')}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        {dialogContent}
      </DialogContent>

      <DialogActions>
        <Box className={classes.dialogActions}>
          <Button className={classes.closeButton} onClick={onClose}>
            {t('testCaseGeneration.history.close')}
          </Button>

          <Box className={classes.rightActions}>
            {showFeedbackAction && (
              <Button
                color="primary"
                variant="outlined"
                disabled={
                  loading ||
                  loadError ||
                  versions.length === 0 ||
                  !testCaseThreadId ||
                  !onFeedback
                }
                onClick={() => {
                  onFeedback?.();
                }}
              >
                {t('testCaseGeneration.feedback.button')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TestCaseVersionHistoryPanel;
